import type { components } from '@connected/api-client/api.generated';
import type { ElectricalProjectInspectionKind } from '@connected/elektra-sdk/validators/electrical-project';

import { api } from '@connected/api-client';
import { toPascalCase } from '@connected/api-client/lib/utils';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { Button } from '@connected/ui/components/button';
import { Checkbox } from '@connected/ui/components/checkbox';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@connected/ui/components/table';
import { Trash2 } from 'lucide-react';

import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';

type InspectionNormSelectionTableProps = {
    norms: components['schemas']['ListElectricalProjectInspectionNormQueryResultItem'][];
    inspectionKinds: ElectricalProjectInspectionKind[];
    projectVersionId: string;
    onRemove: (normId: string) => void;
    isCombinationInspection?: boolean;
};

export function InspectionNormSelectionTable({ norms, inspectionKinds, projectVersionId, onRemove, isCombinationInspection }: InspectionNormSelectionTableProps) {
    const patchMutation = api.useMutation('patch', '/api/v2/electrical/projects/inspectionnorms/patch', {
        onMutate: async (variables) => {
            const body = toPascalCase(variables?.body);

            if (!body) {
                throw new Error('Body is required');
            }

            const queryOptions = elektraAppQueryOptions.project.inspectionPlan.inspectionNormsListOptions({
                electricalProjectVersionId: projectVersionId,
            });
            const previousData = queryClient.getQueryData(queryOptions.queryKey) as components['schemas']['ListElectricalProjectInspectionNormQueryResult'];

            const newData: components['schemas']['ListElectricalProjectInspectionNormQueryResult'] = {
                InspectionNorms: previousData?.InspectionNorms?.map((norm) => {
                    if (norm.ElectricalProjectInspectionNormId === body.ElectricalProjectInspectionNormId) {
                        let updatedInspectionKinds = [...(norm.InspectionKinds || [])];

                        if (body.AddInspectionKind) {
                            updatedInspectionKinds.push(body.AddInspectionKind);
                        }

                        if (body.RemoveInspectionKind) {
                            updatedInspectionKinds = updatedInspectionKinds.filter(
                                kind => kind !== body.RemoveInspectionKind,
                            );
                        }

                        return {
                            ...norm,
                            InspectionKinds: updatedInspectionKinds,
                        };
                    }
                    return norm;
                }),
                Count: previousData?.Count,
            };

            logger.debug({
                message: 'onMutate(inspectionNormPatch)',
                path: '/api/v2/electrical/projects/inspectionnorms/patch',
                inspectionNorm: {
                    previousData,
                    newData,
                    queryOptions,
                    body,
                },
            });

            queryClient.setQueryData(queryOptions.queryKey, newData);

            return { previousData, queryOptions };
        },
        onError: async (_error, _variables, context: any) => {
            if (context?.queryOptions && context?.previousData) {
                queryClient.setQueryData(context.queryOptions.queryKey, context.previousData);
            }
        },
        onSettled: async () => {
            await queryClient.invalidateQueries(
                elektraAppQueryOptions.project.inspectionPlan.inspectionNormsListOptions({
                    electricalProjectVersionId: projectVersionId,
                }),
            );
        },
        scope: {
            id: projectVersionId,
        },
    });

    function handleToggle(normId: string, inspectionKind: components['schemas']['ElectricalInspectionKind'], currentlySelected: boolean) {
        patchMutation.mutate({
            body: {
                ElectricalProjectInspectionNormId: normId,
                ...(currentlySelected
                    ? { RemoveInspectionKind: inspectionKind }
                    : { AddInspectionKind: inspectionKind }),
            },
        });
    }

    // Filter to only show selected norms
    const selectedNorms = norms.filter(norm => norm.Status === 'Selected');

    if (selectedNorms.length === 0) {
        return (
            <div className="text-center text-muted-foreground py-8">
                Geen normen toegevoegd
            </div>
        );
    }

    const showInspectionKindColumns = isCombinationInspection && inspectionKinds.length > 1;

    return (
        <div className="rounded-md border">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[300px]">Norm</TableHead>
                        {showInspectionKindColumns && inspectionKinds.map(inspectionKind => (
                            <TableHead key={inspectionKind} className="text-center">
                                {/* TODO: Translate */}
                                {inspectionKind}
                            </TableHead>
                        ))}
                        <TableHead className="w-[100px]"></TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {selectedNorms.map(norm => (
                        <TableRow key={`${norm.ElectricalProjectInspectionNormId}-row`}>
                            <TableCell>
                                <div className="flex flex-col">
                                    <span className="font-medium">{norm.Name}</span>
                                </div>
                            </TableCell>
                            {showInspectionKindColumns && inspectionKinds.map((inspectionKind) => {
                                const isSelected = norm.InspectionKinds?.includes(inspectionKind) ?? false;

                                return (
                                    <TableCell key={`${norm.ElectricalProjectInspectionNormId}-${inspectionKind}-cell`} className="text-center">
                                        <div className="flex justify-center">
                                            <Checkbox
                                                checked={isSelected}
                                                onCheckedChange={() =>
                                                    handleToggle(
                                                        norm.ElectricalProjectInspectionNormId!,
                                                        inspectionKind,
                                                        isSelected,
                                                    )}
                                            />
                                        </div>
                                    </TableCell>
                                );
                            })}
                            <TableCell className={!showInspectionKindColumns ? 'text-right' : ''}>
                                <div className={!showInspectionKindColumns ? 'flex justify-end' : ''}>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => onRemove(norm.ElectricalProjectInspectionNormId!)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}
